/**
 * ChatWindow Component - HYB Shop Chat
 * Main chat container orchestrating header, messages, and input
 */

import { useRef } from 'react';
import { MessageSquare } from 'lucide-react';
import { cn } from '../../../utils/responsive';
import { EmptyState } from '../../ui/EmptyState';
import { ChatWindowHeader } from './ChatWindowHeader';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ConversationStatus } from '../ConversationStatusBadge';
import { MessageStatus } from '../ReadReceipt';

interface Conversation {
  conversationId: string;
  userId: string;
  userName?: string;
  phoneNumber?: string;
  isGuest?: boolean;
  guestId?: string;
  status?: string | ConversationStatus; // Allow both string and ConversationStatus
  createdAt?: string; // Optional since not displayed in UI
  assignedAdminId?: string; // Optional admin assignment
  lastMessageSender?: 'user' | 'admin'; // Optional sender info
  lastMessagePreview?: string;
  lastMessageAt?: string;
  unreadCount?: number;
}

interface Message {
  id: string;
  isAdmin: boolean;
  text?: string;
  images?: string[];
  type?: 'text' | 'image';
  createdAt?: string;
  status?: MessageStatus;
}

interface ChatWindowProps {
  conversation: Conversation | null;
  messages: Message[];
  draftMessage: string;
  onDraftChange: (value: string) => void;
  onSendMessage: () => void;
  onAttachClick?: () => void;
  onBack?: () => void;
  onRefresh?: () => void;
  isLoadingMessages?: boolean;
  isSending?: boolean;
  isUploading?: boolean;
  isRefreshing?: boolean;
  isUserTyping?: boolean;
  typingUserName?: string;
  isMobile?: boolean;
  className?: string;
}

export function ChatWindow({
  conversation,
  messages,
  draftMessage,
  onDraftChange,
  onSendMessage,
  onAttachClick,
  onBack,
  onRefresh,
  isLoadingMessages = false,
  isSending = false,
  isUploading = false,
  isRefreshing = false,
  isUserTyping = false,
  typingUserName,
  isMobile = false,
  className,
}: ChatWindowProps) {
  const messageEndRef = useRef<HTMLDivElement>(null);

  // No conversation selected - show empty state
  if (!conversation) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <EmptyState
          icon={<MessageSquare className="h-8 w-8" />}
          title="เลือกสนทนาเพื่อเริ่มตอบกลับลูกค้า"
          description="เลือกรายชื่อจากด้านซ้ายเพื่อดูประวัติและตอบข้อความ"
        />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <ChatWindowHeader
        conversation={conversation}
        onBack={onBack}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        isMobile={isMobile}
      />

      {/* Messages */}
      <MessageList
        messages={messages}
        isLoading={isLoadingMessages}
        isUserTyping={isUserTyping}
        typingUserName={typingUserName}
        messageEndRef={messageEndRef}
      />

      {/* Input */}
      <MessageInput
        value={draftMessage}
        onChange={onDraftChange}
        onSend={onSendMessage}
        onAttachClick={onAttachClick}
        isSending={isSending}
        isUploading={isUploading}
      />
    </div>
  );
}

export default ChatWindow;
